import { useEffect, useState } from 'react';
import { Box, Button, IconButton, InputBase, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { FavoriteBorder, Search } from '@mui/icons-material';
import { describeSearch, parseSearch } from './searchParser';
import { text } from '../../theme/colors';

export const INK = '#1e293b';

/**
 * The top row of the browse page, after realtor.com: one wide search box that
 * understands "3 bed Borrowdale under $200k", Save search beside it, and the
 * List / List + map / Map switch on the right (desktop).
 */
export default function SearchHeader({ values = {}, onSearch, onSaveSearch, view = 'split', onViewChange }) {
  const [draft, setDraft] = useState(() => describeSearch(values));
  // A search changed elsewhere (a chip, the Filters panel, Back) rewrites the box.
  const phrase = describeSearch(values);
  useEffect(() => { setDraft(phrase); }, [phrase]);

  const submit = (e) => {
    e?.preventDefault?.();
    onSearch(parseSearch(draft));
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.75 }, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
      <Box
        component="form"
        role="search"
        onSubmit={submit}
        sx={{
          flex: '1 1 420px', minWidth: 0, height: { xs: 50, md: 54 }, display: 'flex', alignItems: 'center',
          px: { xs: 2, md: 2.75 }, border: '1.5px solid #94a3b8', borderRadius: '10px', bgcolor: '#fff',
          '&:focus-within': { borderColor: INK, boxShadow: '0 0 0 3px rgba(30, 41, 59, 0.12)' },
        }}
      >
        <InputBase
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Search by suburb, city, price or feature — e.g. 3 bed Borrowdale under $200k"
          inputProps={{ 'aria-label': 'Search homes by suburb, city, price or feature', maxLength: 120 }}
          sx={{ flex: 1, fontSize: { xs: 16, md: 17 }, color: text.primary, minWidth: 0 }}
        />
        <IconButton type="submit" aria-label="Search" edge="end" sx={{ color: INK }}>
          <Search />
        </IconButton>
      </Box>

      <Button
        onClick={onSaveSearch}
        startIcon={<FavoriteBorder />}
        sx={{
          height: { xs: 44, md: 54 }, px: { xs: 2.25, md: 3.25 }, borderRadius: '10px', bgcolor: INK, color: '#fff',
          textTransform: 'none', fontWeight: 700, fontSize: { xs: 15, md: 16 }, whiteSpace: 'nowrap',
          '&:hover': { bgcolor: '#0f172a' },
        }}
      >
        Save search
      </Button>

      <ToggleButtonGroup
        exclusive
        value={view}
        onChange={(_, v) => v && onViewChange(v)}
        aria-label="How to show results"
        sx={{
          display: { xs: 'none', md: 'flex' }, height: 54, p: '5px', gap: '4px', borderRadius: '12px', bgcolor: '#eef2f1',
          '& .MuiToggleButton-root': {
            border: 0, borderRadius: '8px !important', px: 2.5, textTransform: 'none', fontWeight: 600,
            fontSize: 16, color: INK, whiteSpace: 'nowrap',
          },
          '& .Mui-selected': { bgcolor: '#fff !important', fontWeight: 700, boxShadow: '0 1px 4px rgba(15, 23, 42, 0.16)' },
        }}
      >
        <ToggleButton value="list">List</ToggleButton>
        <ToggleButton value="split">List + map</ToggleButton>
        <ToggleButton value="map">Map</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}
